/**
 * PF Revamp — effective workspace resolver for the entry surfaces.
 *
 * The property endpoints (search / create / detail / patch) are all workspace
 * scoped. For a client that workspace is their own property workspace
 * (`useUserData().userPropertyWorkspaceId`). For an admin ordering on behalf of
 * a client it is the workspace picked in the client-selection stage of the
 * property drawer (`propertySlice.targetWorkspaceId`) — admins have no property
 * workspace of their own, so reading `userPropertyWorkspaceId` there resolved
 * to `''` and silently disabled every property query (`enabled: !!workspaceId`).
 *
 * Fallback order: admin target workspace → acting user's property workspace.
 * Clients never write `targetWorkspaceId` (the admin gate is the only writer,
 * and draft rehydration only carries it for admin-scoped drafts), so the plain
 * fallback is safe without a role check.
 */
import { useUserData } from 'components/contexts/UserDataContext'
import { usePfStore } from 'components/pages/PurchaseFlowRevamp/store'

/**
 * Resolves the workspace ID every property query in the entry flow must use.
 *
 * @returns the admin-selected target workspace when set, otherwise the acting
 * user's property workspace. `''` while unresolved (mirrors `useUserData`).
 */
export function usePfEffectiveWorkspaceId(): string {
  const { userPropertyWorkspaceId } = useUserData()
  const adminTargetWorkspaceId = usePfStore((s) => s.targetWorkspaceId)

  // `||` (not `??`): treat both `null` and `''` as "no admin target".
  return adminTargetWorkspaceId || userPropertyWorkspaceId
}
